import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { AppDispatch } from '../../../store/store';
import { KID_MODE_CODES } from '../api/context';
import { exitKidMode, exitKidModeMfa, KidModeFailure } from '../store/contextSlice';

interface Props {
  onClose: () => void;
  /** Called once the server has lifted the lock and the Parent session is back. */
  onExited: () => void;
}

/** The words for a failure; the server decides which failure it was. */
export function exitFailureMessage(failure: KidModeFailure | undefined, step: Step): string {
  if (failure?.code === KID_MODE_CODES.reauthFailed) {
    return step === 'password' ? 'Esa contraseña no es correcta.' : 'Ese código no es correcto.';
  }
  return 'No pudimos comprobar tu identidad. Inténtalo otra vez.';
}

type Step = 'password' | 'code';

/**
 * DEC-S2-S01-04 / BR-04 — the way back to the Parent.
 *
 * Asks for the account's password, and the authenticator code where the account
 * has one. The identity provider answers, and counts the wrong ones. Nothing
 * Parent-side loads until the server has answered and the fresh session is in
 * place. A forgotten password has the provider's own reset, which is what the
 * family PIN this replaced never had. The words are for the adult who was called
 * over.
 */
const KidExitDialog: React.FC<Props> = ({ onClose, onExited }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = useState<Step>('password');
  const [value, setValue] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  const submitPassword = async () => {
    const result = await dispatch(exitKidMode(value));
    if (!exitKidMode.fulfilled.match(result)) {
      setError(
        exitFailureMessage(exitKidMode.rejected.match(result) ? result.payload : undefined, step),
      );
      return;
    }
    if ('challenge' in result.payload) {
      setChallenge(result.payload.session);
      setStep('code');
      return;
    }
    if (result.payload.kidMode) setError(exitFailureMessage(undefined, step));
    else onExited();
  };

  const submitCode = async () => {
    const result = await dispatch(exitKidModeMfa({ session: challenge ?? '', code: value }));
    if (exitKidModeMfa.fulfilled.match(result) && !result.payload.kidMode) {
      onExited();
      return;
    }
    setError(
      exitFailureMessage(exitKidModeMfa.rejected.match(result) ? result.payload : undefined, step),
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || value.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await (step === 'password' ? submitPassword() : submitCode());
    } catch {
      setError(exitFailureMessage(undefined, step));
    } finally {
      setBusy(false);
      setValue('');
    }
  };

  const onPassword = step === 'password';

  return (
    <div className="kid-dialog-ov" role="presentation">
      <form
        className="kid-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kid-exit-title"
        onSubmit={submit}
      >
        <h2 id="kid-exit-title">Esto es para un adulto</h2>
        <p>
          {onPassword
            ? 'Para salir del espacio del alumno, escribe la contraseña de tu cuenta.'
            : 'Escribe el código de 6 dígitos de tu app de autenticación.'}
        </p>
        <label htmlFor="kid-exit-secret">{onPassword ? 'Contraseña' : 'Código'}</label>
        <input
          key={step}
          id="kid-exit-secret"
          ref={inputRef}
          type={onPassword ? 'password' : 'text'}
          inputMode={onPassword ? undefined : 'numeric'}
          // Off for both: a browser that fills this in hands the way out to
          // whoever is holding the device.
          autoComplete="off"
          maxLength={onPassword ? undefined : 6}
          value={value}
          onChange={(e) =>
            setValue(onPassword ? e.target.value : e.target.value.replace(/\D/g, ''))
          }
        />
        {error && (
          <p className="kid-dialog-err" role="alert">
            {error}
          </p>
        )}
        <div className="kid-dialog-b">
          <button type="button" className="cb cb-o" onClick={onClose}>
            Seguir aquí
          </button>
          <button type="submit" className="cb cb-p" disabled={busy || value.length === 0}>
            Salir
          </button>
        </div>
      </form>
    </div>
  );
};

export default KidExitDialog;
